import { Button, Typography, Box } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import authService from "../../services/auth.service";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ResponsiveRow } from "../../constants/layout.constants";
import { logout } from "../../actions/auth";

const SessionTimeoutModal = () => {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.auth.user);
  const refreshTokenAlert = useSelector((state) => state.auth.refreshTokenAlert);

  const [open, setOpen] = useState(refreshTokenAlert);
  const dispatch = useDispatch();

  const handleStayLogedIn = () => {
    authService.getAccessToken();
  };

  // Reset timer when modal opens
  useEffect(() => {
    if (open && user?.expiresAt) {
      // get the difference in time between the server and client
      const skew = Number(localStorage.getItem('clock_skew')) || 0;

      const updateTimer = () => {
        // Get the local time and "steer" it by the skew
        const correctedNow = Date.now() + skew;

        // Calculate difference (both are now in "Server Time")
        const secToExpire = (user.expiresAt - correctedNow) / 1000;

        setSecondsLeft(Math.max(0, Math.floor(secToExpire)));
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [open]);

  // Countdown logic
  useEffect(() => {
    let timer;
    if (open && secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prev) => Math.max(prev - 1, 0));
      }, 1000);
    } else if (open && secondsLeft === 0) {
      if(isLoggedIn) {
        dispatch(logout());
      }; // Dispatch logout action when timer reaches 0
    }

    return () => clearInterval(timer);
  }, [open, secondsLeft]);

  useEffect(() => {
    if (refreshTokenAlert) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [refreshTokenAlert]);

  // Format seconds into MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs}`;
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        borderRadius: "20px",
        padding: "2px 10px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        width: "fit-content",
        margin: "auto",
        zIndex: 1300,
        position: "absolute",
        bottom: "-35px",
        height: "fit-content",
      }}
    >
      <Typography variant="body1">
        <WarningAmberIcon color="warning" />
        Your session is about to expire. For your security, you will be logged
        out automatically.
      </Typography>
      <Box
        sx={{ height: "fit-content", width: "fit-content", marginInline: 2 }}
      >
        <Button
          onClick={handleStayLogedIn}
          variant="contained"
          color="success"
          autoFocus
          sx={{ wordWrap: "nowrap" }}
        >
          Keep Me Logged In
        </Button>
      </Box>
      <ResponsiveRow
        sx={{
          bgcolor: secondsLeft < 20 ? "#fef2f2" : "#f8fafc",
          borderRadius: 3,
          textAlign: "center",
          border: "2px solid",
          borderColor: secondsLeft < 20 ? "error.light" : "grey.200",
          transition: "all 0.3s ease",
          height: "fit-content",
          width: "fit-content",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: "bold" }}
        >
          Remaining
        </Typography>
        " "
        <Typography
          variant="h2"
          sx={{
            fontFamily: "monospace",
            fontWeight: "800",
            color: secondsLeft < 60 ? "error.main" : "primary.main", // Change the color a minute to
            lineHeight: 1,
          }}
        >
          {formatTime(secondsLeft)}
        </Typography>
      </ResponsiveRow>
    </Box>
  );
};

export default SessionTimeoutModal;
